import { create } from "@bufbuild/protobuf";
import { RoomRole } from "@puzzleini/room-server/protocol";
import { ConnectionTicketSchema, type ConnectionTicket } from "@puzzleini/room-server/protocol";

const ROOM_INVITE_STORAGE_PREFIX = "puzzle-muzzle-room-invite:";
const ROOM_TICKET_STORAGE_PREFIX = "puzzle-muzzle-room-ticket:";
const ROOM_INACTIVITY_RECOVERY_STORAGE_KEY =
  "puzzle-muzzle:inactivity-recovery";

export function clearStoredRoomAdmissionCredentials(): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(ROOM_TICKET_STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) window.sessionStorage.removeItem(key);
    window.sessionStorage.removeItem(ROOM_INACTIVITY_RECOVERY_STORAGE_KEY);
  } catch {
    // Local socket closure still prevents continued use in this tab.
  }
}

export function readStoredRoomTicket(
  roomId: string,
): ConnectionTicket | undefined {
  try {
    const value = window.sessionStorage.getItem(
      `${ROOM_TICKET_STORAGE_PREFIX}${roomId}`,
    );
    if (!value) return undefined;
    const stored = JSON.parse(value) as {
      expiresAtUnixMs?: string;
      playerId?: string;
      role?: number;
      token?: string;
      websocketUrl?: string;
    };
    const expiresAtUnixMs = BigInt(stored.expiresAtUnixMs ?? "0");
    if (
      expiresAtUnixMs <= BigInt(Date.now()) ||
      !stored.playerId ||
      !stored.token ||
      !stored.websocketUrl
    ) {
      removeStoredRoomTicket(roomId);
      return undefined;
    }
    return create(ConnectionTicketSchema, {
      expiresAtUnixMs,
      playerId: stored.playerId,
      role: stored.role === RoomRole.SPECTATOR
        ? RoomRole.SPECTATOR
        : RoomRole.PLAYER,
      token: stored.token,
      websocketUrl: stored.websocketUrl,
    });
  } catch {
    removeStoredRoomTicket(roomId);
    return undefined;
  }
}

export function storeRoomTicket(
  roomId: string,
  ticket: ConnectionTicket,
): void {
  try {
    window.sessionStorage.setItem(
      `${ROOM_TICKET_STORAGE_PREFIX}${roomId}`,
      JSON.stringify({
        expiresAtUnixMs: ticket.expiresAtUnixMs.toString(),
        playerId: ticket.playerId,
        role: ticket.role,
        token: ticket.token,
        websocketUrl: ticket.websocketUrl,
      }),
    );
  } catch {
    // The in-memory Room connection remains usable when storage is unavailable.
  }
}

export function removeStoredRoomTicket(roomId: string): void {
  try {
    window.sessionStorage.removeItem(
      `${ROOM_TICKET_STORAGE_PREFIX}${roomId}`,
    );
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
}

export function readStoredRoomInvitationCode(roomId: string): string {
  try {
    return window.sessionStorage.getItem(
      `${ROOM_INVITE_STORAGE_PREFIX}${roomId}`,
    ) ?? "";
  } catch {
    return "";
  }
}

export function storeRoomInvitationCode(
  roomId: string,
  invitationCode: string,
): void {
  try {
    window.sessionStorage.setItem(
      `${ROOM_INVITE_STORAGE_PREFIX}${roomId}`,
      invitationCode,
    );
  } catch {
    // Authenticated members can still restore without the Invitation capability.
  }
}

export function removeStoredRoomInvitationCode(roomId: string): void {
  try {
    window.sessionStorage.removeItem(
      `${ROOM_INVITE_STORAGE_PREFIX}${roomId}`,
    );
  } catch {
    // Storage may be unavailable in privacy-restricted browser contexts.
  }
}

export function removeStoredRoomAccess(roomId: string): void {
  if (!roomId) return;
  removeStoredRoomInvitationCode(roomId);
  removeStoredRoomTicket(roomId);
}
